import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Microphone, Stop, SpinnerGap } from "@phosphor-icons/react";

const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";

/**
 * Tap-to-record voice notes transcribed with Whisper.
 * - mode="note": posts to /in-house-portal/{type}/{id}/voice-note and calls onSaved(note)
 * - mode="dictate": posts to /in-house-portal/transcribe and calls onText(text) (fills a textarea)
 */
export default function VoiceNoteRecorder({ entityType, entityId, mode = "note", onSaved, onText, size = "sm", label }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const supported = typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  useEffect(() => () => { clearInterval(timer.current); try { recRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ } }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) { setState("idle"); toast.error("Recording was too short"); return; }
        await upload(blob);
      };
      rec.start(250);
      recRef.current = rec;
      setSecs(0); setState("recording");
      timer.current = setInterval(() => setSecs((s) => { if (s >= 179) { try { rec.stop(); } catch (_) { /* noop */ } } return s + 1; }), 1000);
    } catch (e) {
      toast.error("Microphone access was blocked — allow it in your browser settings");
    }
  };
  const stop = () => { try { recRef.current?.stop(); } catch (_) { /* noop */ } setState("uploading"); };
  const upload = async (blob) => {
    setState("uploading");
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
      fd.append("audio", new File([blob], `voice-note.${ext}`, { type: blob.type }));
      if (mode === "dictate") {
        const { data } = await API.post("/in-house-portal/transcribe", fd);
        onText && onText(data.text);
        toast.success("Transcribed");
      } else {
        fd.append("kind", "note");
        const { data } = await API.post(`/in-house-portal/${entityType}/${entityId}/voice-note`, fd);
        onSaved && onSaved(data.note);
        toast.success("Voice note saved to the job");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't transcribe the recording");
    } finally { setState("idle"); }
  };

  if (!supported) return null;
  if (state === "recording") {
    return <Button size={size} variant="destructive" onClick={stop} data-testid="voice-stop-btn"><Stop size={14} className="mr-1" weight="fill" /> Stop · {String(Math.floor(secs / 60)).padStart(1, "0")}:{String(secs % 60).padStart(2, "0")}</Button>;
  }
  if (state === "uploading") {
    return <Button size={size} variant="outline" disabled data-testid="voice-uploading"><SpinnerGap size={14} className="mr-1 animate-spin" /> Transcribing…</Button>;
  }
  return <Button size={size} variant="outline" onClick={start} data-testid="voice-record-btn"><Microphone size={14} className="mr-1" /> {label || (mode === "dictate" ? "Dictate" : "Voice note")}</Button>;
}
